/**
 * >>> **HER FACE-AWARE TIER, ON A MODEL THIS HOUSE SERVES ITSELF.** <<<
 *
 * Her decision (2026-08-27): *"self-host the face-mesh model: the MediaPipe model files ride in this
 * repo/bundle served from OUR domain like any asset, downloaded once at build time, never fetched
 * from a vendor at broadcast."* That is why `/mediapipe/` exists in `public/`, and it is the whole
 * reason this tier is allowed to exist at all — every member-facing walk in this repo asserts ZERO
 * OFFSITE REQUESTS, and a model pulled from a CDN while she is live would break that law on the one
 * screen her audience is watching.
 *
 * **WHAT IT PRODUCES IS A HANDFUL OF NUMBERS, NOT A TEXTURE.** The shader needs to know where her
 * face is and where her eyes and mouth are, so it can soften skin and leave detail alone. An ellipse
 * and three circles say that in twelve floats, uploaded as uniforms — where a mask texture would
 * cost an upload per frame on a phone that is already running an encoder.
 *
 * **EVERY FAILURE IS THE FALLBACK TIER, NEVER A BROKEN CAMERA (R19, and her own tiering clause).**
 * The import failing, the model 404ing, the runtime refusing, no face in frame: all of them answer
 * `null`, and `null` means the shader smooths full-frame exactly as it did before this file existed.
 * There is no state in which turning her filter on gives her a black screen.
 */
export type FaceMask = {
  /** Face oval: centre and radii, in 0..1 texture space. */
  cx: number; cy: number; rx: number; ry: number;
  /** Protected detail, each a circle in the same space: the shader keeps these sharp. */
  eyeL: [number, number, number];
  eyeR: [number, number, number];
  mouth: [number, number, number];
};

export type FaceTracker = {
  /** The most recent mask, or null when there is no face and the full-frame tier should carry it. */
  read: () => FaceMask | null;
  stop: () => void;
};

// The landmark indices this house reads, from MediaPipe's own 468-point mesh. Named rather than
// scattered as magic numbers, because a wrong index here is a filter that softens an eyebrow.
const OVAL = [10, 152, 234, 454];        // top, chin, left cheek, right cheek
const EYE_L = [33, 133, 159, 145];       // outer, inner, upper lid, lower lid
const EYE_R = [362, 263, 386, 374];
const MOUTH = [61, 291, 13, 14];         // corners, upper lip, lower lip

function circleOf(points: { x: number; y: number }[], pad: number): [number, number, number] {
  let minX = 1; let maxX = 0; let minY = 1; let maxY = 0;
  for (const p of points) {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
  }
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const r = Math.max(maxX - minX, maxY - minY) / 2 + pad;
  return [cx, cy, r];
}

/**
 * Start tracking, or answer `null` and let the caller stay on the full-frame tier.
 *
 * **IT TRACKS AT ITS OWN PACE, NOT THE CAMERA'S.** A face does not move meaningfully between two
 * frames at 30fps, and running the mesh on every one is how a beauty filter turns a phone into a
 * hand-warmer and drops the broadcast to 12fps. Twelve times a second is imperceptible on a face and
 * a fraction of the cost.
 */
export async function startFaceTracker(video: HTMLVideoElement): Promise<FaceTracker | null> {
  let mod: typeof import("@mediapipe/tasks-vision");
  try {
    mod = await import("@mediapipe/tasks-vision");
  } catch {
    return null;   // the bundle could not load it: the fallback tier carries her
  }

  // >>> **THE SIMD CHECK IS A REAL DEFECT CLOSED, NOT A PRECAUTION (found 2026-08-27).** <<<
  //
  // `FilesetResolver.forVisionTasks(base)` picks its own filenames at runtime: with WebAssembly SIMD
  // it asks for `vision_wasm_internal.js`, and WITHOUT it, `vision_wasm_nosimd_internal.js`. This
  // house serves only the first pair. So on any browser lacking SIMD the resolver was requesting a
  // file that is not there, taking a 404, throwing, and dropping her to the full-frame tier —
  // **silently, with a wasted round trip, and with no way to tell it from a phone that simply has no
  // face in shot.**
  //
  // **THE ANSWER IS TO ASK FIRST RATHER THAN TO SHIP THE OTHER 11MB**, and that is her own tiering
  // clause rather than thrift: a phone without SIMD is a weak phone, the no-SIMD runtime would run
  // the mesh in software, and a face tier that stutters is worse for her members than the full-frame
  // tier that does not. **A weak device is supposed to land on the tier below. Now it lands there
  // deliberately, on the first line, instead of by accident on a 404.**
  try {
    if (!(await mod.FilesetResolver.isSimdSupported())) return null;
  } catch {
    return null;
  }

  // The vendor's own class has a protected constructor, so `InstanceType` cannot name it. What this
  // file actually uses is two methods, and naming those is both honest and narrower than the class.
  type Landmarker = { detectForVideo: (v: HTMLVideoElement, t: number) => { faceLandmarks?: { x: number; y: number }[][] }; close?: () => void };
  let landmarker: Landmarker | null = null;
  // BOTH PATHS ARE OURS. `/mediapipe/` is served by this house, so nothing here reaches a vendor.
  let fileset: Awaited<ReturnType<typeof mod.FilesetResolver.forVisionTasks>>;
  try {
    fileset = await mod.FilesetResolver.forVisionTasks("/mediapipe");
  } catch {
    return null;
  }
  // >>> **GPU FIRST, THEN CPU — TWO TRIES, BECAUSE ONE WAS COSTING HER THE WHOLE TIER.** <<<
  //
  // The delegate was `"GPU"` with no retry, so a browser that refuses a WebGL compute delegate —
  // which some mobile Safari builds and every locked-down or software-rendered context does — lost
  // the face tier outright, on a phone perfectly able to run the mesh on its CPU twelve times a
  // second. **Two facts, not one (R18): *this device cannot run the model* and *this device cannot
  // run it on the GPU* are different, and only the first is a reason to fall back.**
  for (const delegate of ["GPU", "CPU"] as const) {
    try {
      landmarker = await mod.FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: "/mediapipe/face_landmarker.task", delegate },
        runningMode: "VIDEO",
        numFaces: 1,
      }) as unknown as Landmarker;
      break;
    } catch {
      landmarker = null;   // try the next delegate; the loop running out IS the fallback
    }
  }
  // A model that 404s, a runtime a browser refuses on both delegates: the same answer, and the same
  // answer is the tier below.
  if (!landmarker) return null;

  let mask: FaceMask | null = null;
  let live = true;
  let timer = 0;

  const tick = () => {
    if (!live || !landmarker) return;
    try {
      if (video.readyState >= 2 && video.videoWidth) {
        const res = landmarker.detectForVideo(video, performance.now());
        const pts = res?.faceLandmarks?.[0];
        if (pts && pts.length > 400) {
          const oval = OVAL.map((i) => pts[i]);
          let minX = 1; let maxX = 0; let minY = 1; let maxY = 0;
          for (const p of oval) {
            minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
            minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
          }
          mask = {
            cx: (minX + maxX) / 2,
            cy: (minY + maxY) / 2,
            // A little wider than the landmarks: the oval is the face's outline, and skin runs to
            // the jaw and the hairline rather than stopping on the dots.
            rx: ((maxX - minX) / 2) * 1.18,
            ry: ((maxY - minY) / 2) * 1.12,
            eyeL: circleOf(EYE_L.map((i) => pts[i]), 0.012),
            eyeR: circleOf(EYE_R.map((i) => pts[i]), 0.012),
            mouth: circleOf(MOUTH.map((i) => pts[i]), 0.010),
          };
        } else {
          // NO FACE IS A REAL ANSWER (R18): she has stepped out of frame, and the full-frame tier is
          // what should carry the picture until she is back — not a stale mask over an empty chair.
          mask = null;
        }
      }
    } catch {
      mask = null;
    }
  };
  timer = window.setInterval(tick, 80);

  return {
    read: () => mask,
    stop: () => {
      live = false;
      window.clearInterval(timer);
      try { landmarker?.close?.(); } catch { /* a runtime that will not close is not her problem */ }
      landmarker = null;
    },
  };
}
